import React, { useState, useEffect } from 'react';
import { IconBrandTwitter } from '@tabler/icons-react';
import { format, parseISO } from 'date-fns';
import { Avatar, AvatarImage, AvatarFallback } from '../../../ui/avatar';
import { Badge } from '../../../ui/badge';
import { Spinner } from '../../../ui/spinner';
import { Separator } from '../../../ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../../../ui/dropdown-menu';
import { useReviewAPI } from '../hooks/useReviewAPI';
import TwitterPostCard from './TwitterPostCard';
import { getCreatorDisplayName, getCreatorTwitterUsername } from '../../../../utils/creatorHelpers';

/**
 * Content Panel Component
 * Displays creator information and campaign posts
 */
const ContentPanel = ({ creator, scoringData = null, onStatusUpdate }) => {
  const [campaignPosts, setCampaignPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(creator?.status || 'NeedsToSet');
  const [pagination, setPagination] = useState({ total: 0, limit: 1000, skip: 0, hasMore: false });
  const [loadingMore, setLoadingMore] = useState(false);

  const api = useReviewAPI();

  // Load campaign posts when creator changes
  useEffect(() => {
    if (creator?._id) {
      loadCampaignPosts(creator._id);
    }
  }, [creator?._id]);

  const loadCampaignPosts = async (creatorId, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setCampaignPosts([]);
        setPagination({ total: 0, limit: 10, skip: 0, hasMore: false });
      }
      setError(null);

      const skip = append ? campaignPosts.length : 0;

      const response = await api.getCreatorCampaignPosts(creatorId, {
        limit: 1000,
        skip,
        includeTwitterContent: false
      });

      if (response && response.posts) {
        if (append) {
          setCampaignPosts(prev => [...prev, ...response.posts]);
        } else {
          setCampaignPosts(response.posts);
        }

        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        const posts = Array.isArray(response) ? response : [];
        setCampaignPosts(append ? prev => [...prev, ...posts] : posts);
      }
    } catch (error) {
      console.error('Error loading campaign posts:', error);
      setError(error.message);
      if (!append) {
        setCampaignPosts([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMorePosts = () => {
    if (pagination.hasMore && !loadingMore && creator?._id) {
      loadCampaignPosts(creator._id, true);
    }
  };

  // Format large numbers
  const formatNumber = (num) => {
    if (!num && num !== 0) return 'N/A';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'Unknown';
    try {
      return format(parseISO(date), 'MMM d, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  // Get Twitter URL
  const getTwitterUrl = (handle) => {
    if (!handle) return '#';
    const cleanHandle = handle.startsWith('@') ? handle.slice(1) : handle;
    return `https://twitter.com/${cleanHandle}`;
  };

  // Get profile image from creator object
  const getProfileImage = (creator) => {
    const primaryImage = creator.twitter?.profileImageUrl;
    const fallbackImage = creator.twitter?.username
      ? `https://unavatar.io/twitter/${creator.twitter.username}`
      : null;
    return primaryImage || fallbackImage;
  };

  // Helper function to get badge variant based on status
  const getStatusBadgeVariant = (status) => {
    if (status === 'Strong Enough') return 'success';
    if (status === 'Not Strong Enough') return 'destructive';
    if (status === 'Strong Enough If We Need a Lot') return 'warning';
    if (status?.includes('High') || status?.includes('Priority')) return 'default';
    if (status?.includes('Approved')) return 'success';
    if (status?.includes('Last')) return 'warning';
    return 'secondary';
  };

  const handleStatusSelect = (status) => {
    setSelectedStatus(status);
    if (onStatusUpdate) onStatusUpdate(status);
  };

  const displayName = getCreatorDisplayName(creator);
  const twitterHandle = getCreatorTwitterUsername(creator);

  return (
    <div>
      {/* Creator Header — compact single row, wraps on mobile */}
      <div className="glass-card p-3 mb-3">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10 flex-shrink-0">
            <AvatarImage src={getProfileImage(creator)} alt={displayName} />
            <AvatarFallback>
              {displayName ? displayName.replace(/^@/, '').charAt(0).toUpperCase() : '?'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h6 className="font-semibold text-sm text-text-primary truncate">
                {displayName}
              </h6>
              <a
                href={getTwitterUrl(twitterHandle)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center no-underline flex-shrink-0"
              >
                <IconBrandTwitter size={13} className="mr-0.5" style={{ color: '#1DA1F2' }} />
                <span className="text-xs text-accent">
                  {twitterHandle}
                </span>
              </a>
            </div>
          </div>

          {/* Stats inline — hidden on mobile, shown as row below */}
          <div className="hidden sm:flex items-center gap-4 flex-shrink-0 text-center">
            <div>
              <p className="font-semibold text-sm text-text-primary leading-tight">
                {formatNumber(creator.twitter?.followers || creator.followersCount || creator.followers || 0)}
              </p>
              <span className="text-[0.625rem] text-text-tertiary">Followers</span>
            </div>
            <div>
              <p className="font-semibold text-sm text-text-primary leading-tight">
                {campaignPosts.length > 0 ? campaignPosts.length : (creator.analytics?.overall?.totalCampaigns || creator.analytics?.campaigns?.length || creator.totalCampaigns || creator.campaigns?.length || 0)}
              </p>
              <span className="text-[0.625rem] text-text-tertiary">Campaigns</span>
            </div>
            <div>
              <p className="font-semibold text-sm text-text-primary leading-tight">
                {scoringData ? `${scoringData.takeoverScore}/15` : 'N/A'}
              </p>
              <span className="text-[0.625rem] text-text-tertiary">Score</span>
            </div>
            <div className="text-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button>
                    <Badge
                      variant={getStatusBadgeVariant(selectedStatus || creator.status)}
                      className="cursor-pointer font-semibold text-xs h-[22px] hover:opacity-80"
                    >
                      {selectedStatus || creator.status || 'NeedsToSet'}
                    </Badge>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[280px]">
                <DropdownMenuLabel className="text-xs text-text-secondary">
                  REVIEW STATUSES
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => handleStatusSelect('Strong Enough')}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-accent" />
                    Strong Enough
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleStatusSelect('Not Strong Enough')}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-gray-400" />
                    Not Strong Enough
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleStatusSelect('Strong Enough If We Need a Lot')}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-gray-400" />
                    Strong Enough If We Need a Lot
                  </div>
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuLabel className="text-xs text-text-secondary">
                  OTHER STATUSES
                </DropdownMenuLabel>
                {['High Priority', 'Approved', 'Last Approvals', 'In Reviewing', 'NeedsToSet'].map(status => (
                  <DropdownMenuItem key={status} onClick={() => handleStatusSelect(status)}>
                    {status}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          </div>
        </div>
        {/* Mobile stats row */}
        <div className="flex sm:hidden items-center gap-3 mt-2 pt-2 border-t border-glass-border">
          <div className="flex-1 text-center">
            <p className="font-semibold text-sm text-text-primary leading-tight">
              {formatNumber(creator.twitter?.followers || creator.followersCount || creator.followers || 0)}
            </p>
            <span className="text-[0.625rem] text-text-tertiary">Followers</span>
          </div>
          <div className="flex-1 text-center">
            <p className="font-semibold text-sm text-text-primary leading-tight">
              {campaignPosts.length > 0 ? campaignPosts.length : (creator.analytics?.overall?.totalCampaigns || creator.analytics?.campaigns?.length || creator.totalCampaigns || creator.campaigns?.length || 0)}
            </p>
            <span className="text-[0.625rem] text-text-tertiary">Campaigns</span>
          </div>
          <div className="flex-1 text-center">
            <p className="font-semibold text-sm text-text-primary leading-tight">
              {scoringData ? `${scoringData.takeoverScore}/15` : 'N/A'}
            </p>
            <span className="text-[0.625rem] text-text-tertiary">Score</span>
          </div>
          <div className="text-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button>
                  <Badge
                    variant={getStatusBadgeVariant(selectedStatus || creator.status)}
                    className="cursor-pointer font-semibold text-xs h-[22px] hover:opacity-80"
                  >
                    {selectedStatus || creator.status || 'NeedsToSet'}
                  </Badge>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[280px]">
                <DropdownMenuLabel className="text-xs text-text-secondary">
                  REVIEW STATUSES
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => handleStatusSelect('Strong Enough')}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-accent" />
                    Strong Enough
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleStatusSelect('Not Strong Enough')}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-gray-400" />
                    Not Strong Enough
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleStatusSelect('Strong Enough If We Need a Lot')}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-gray-400" />
                    Strong Enough If We Need a Lot
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-text-secondary">
                  OTHER STATUSES
                </DropdownMenuLabel>
                {['High Priority', 'Approved', 'Last Approvals', 'In Reviewing', 'NeedsToSet'].map(status => (
                  <DropdownMenuItem key={status} onClick={() => handleStatusSelect(status)}>
                    {status}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Campaign Posts Section */}
      <div>
        <h6 className="text-sm font-semibold text-text-primary mb-2 flex items-center">
          Campaign Posts
          <Badge className="ml-2 h-5 text-xs">{campaignPosts.length}</Badge>
        </h6>

        {loading ? (
          <div>
            {[...Array(3)].map((_, index) => (
              <TwitterPostCard
                key={`skeleton-${index}`}
                post={{}}
                loading={true}
                index={index}
              />
            ))}
          </div>
        ) : error ? (
          <div className="p-8 text-center border border-glass-border bg-surface-1">
            <h6 className="font-semibold mb-2 text-text-primary">
              Warning: Error Loading Posts
            </h6>
            <p className="text-sm text-text-secondary">{error}</p>
          </div>
        ) : campaignPosts.length === 0 ? (
          <div className="p-6 text-center border border-dashed border-glass-border bg-surface-1">
            <IconBrandTwitter size={36} className="text-text-tertiary opacity-50 mb-2 mx-auto" />
            <p className="text-sm text-text-tertiary">
              No campaign posts yet
            </p>
          </div>
        ) : (
          <>
            <div className="pr-2">
              {campaignPosts.map((post, index) => (
                <TwitterPostCard
                  key={post._id || index}
                  post={post}
                  loading={false}
                  index={index}
                />
              ))}
            </div>

            {/* Post Count */}
            {campaignPosts.length > 0 && (
              <div className="mt-6 p-4 text-center bg-surface-1 border border-glass-border">
                <p className="text-sm text-text-secondary font-medium">
                  Total: {campaignPosts.length} campaign post{campaignPosts.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ContentPanel;
